import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { EN, directionFor, type Direction } from '@miraki/i18n';

/**
 * D47, CR-02 — the customer-facing half of the portal speaks the customer's
 * language. The admin console does not, by decision: Miraki's own staff work in
 * English, and translating 32 internal screens buys nothing a trade customer
 * ever sees. An ESLint rule holds that boundary rather than a convention.
 *
 * **Languages are data.** The list, and the strings behind each one, come from
 * the API — an administrator adds a language on A-34 and types its translations,
 * with no release. What is *not* data is the set of message keys: those live in
 * `packages/i18n` because a key is a promise that some component renders it, and
 * one invented at runtime would be a translation nobody ever sees. The same
 * catalog-in-code, grant-in-data split as permissions and pages.
 *
 * English is bundled, not fetched. It is the source text, so the interface
 * renders before any network call and every untranslated key falls back to it —
 * which is what makes adding a language strictly additive rather than a way to
 * blank a screen.
 */

export interface LocaleOption {
  code: string;
  name: string;
  endonym: string;
  direction: Direction;
}

/**
 * Pre-authentication the portal has no idea who you are — D1 makes sign-in one
 * shared front door — so a choice made on the public pages lives in the browser.
 */
const STORAGE_KEY = 'miraki.locale';

export function storedLocale(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    // Safari in private mode throws rather than returning null.
    return null;
  }
}

function storeLocale(code: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, code);
  } catch { /* a browser refusing storage still gets the language, just not next time */ }
}

/**
 * Applied to `<html>`, not to a container: `dir` has to be on the root for form
 * controls, scrollbars and the browser's own text selection to flip with the
 * page. `lang` goes with it, because a screen reader picks its voice from it.
 */
export function applyDocumentLocale(code: string, direction: Direction): void {
  const el = document.documentElement;
  el.lang = code;
  el.dir = direction;
}

/** The languages this portal publishes. Public, because the switcher is pre-auth. */
export async function fetchLocales(): Promise<LocaleOption[]> {
  try {
    const res = await fetch('/api/locales', { credentials: 'same-origin' });
    if (!res.ok) return [];
    return (await res.json()) as LocaleOption[];
  } catch {
    // The switcher degrades to English-only rather than the page failing to load.
    return [];
  }
}

const loaded = new Set<string>(['en']);

/**
 * Switches language, fetching the catalog the first time it is needed.
 *
 * A failed fetch leaves the language unchanged rather than switching to an empty
 * one: falling back key-by-key is correct for a *partial* translation, but a
 * whole missing catalog means the network failed, and silently rendering English
 * under an Arabic label is worse than not switching.
 */
export async function setLocale(code: string, direction?: Direction): Promise<void> {
  let dir = direction;
  if (code !== 'en' && !loaded.has(code)) {
    const res = await fetch(`/api/locales/${encodeURIComponent(code)}/messages`, {
      credentials: 'same-origin',
    });
    if (!res.ok) throw new Error(`No messages for ${code}`);
    const body = (await res.json()) as { direction: Direction; messages: Record<string, string> };
    // Flat keys, `deep: false`: the API sends `shop.catalog.title`, already the
    // address i18next uses. Re-nesting it would only invite the two to disagree.
    i18next.addResourceBundle(code, 'translation', body.messages, false, true);
    loaded.add(code);
    dir ??= body.direction;
  }
  storeLocale(code);
  await i18next.changeLanguage(code);
  applyDocumentLocale(code, dir ?? directionFor(code));
}

const initial = storedLocale() ?? 'en';

void i18next.use(initReactI18next).init({
  // English is the only bundled catalog, and the fallback for every other.
  resources: { en: { translation: EN } },
  lng: initial,
  fallbackLng: 'en',
  returnEmptyString: false,
  interpolation: { escapeValue: false },
});

applyDocumentLocale(initial, directionFor(initial));

/**
 * A stored language has to be re-fetched on every load: the catalog lives on the
 * server, so the bundle knows nothing about it until it is asked. Failure here
 * is silent and leaves English — the alternative is a page that will not render
 * because a translation server is down.
 */
if (initial !== 'en') {
  void setLocale(initial).catch(() => applyDocumentLocale('en', 'ltr'));
}

export default i18next;
